import {Component, Vue} from "vue-property-decorator";
import SockJS from "sockjs-client";
import Stomp, {Message, Subscription} from "webstomp-client";
import {EventBus} from "@/lib/event-bus";
import {AXIOS} from "@/lib/http-common";
import ApplicationToolbar from "@/components/application/ApplicationToolbar.vue";
import ApplicationOverlay from "@/components/application/ApplicationOverlay.vue";
import NotificationPopup from "@/components/application/NotificationPopup.vue";
import StartupModal from "@/components/application/StartupModal";
import {debounce} from "@/lib/util";
import ErrorLog from "./components/error-log/model/ErrorLog";
import {EVENTBUS_TOPICS, STOMP_TOPICS} from "./lib/constants";
import UserActionResultPopup from "@/components/application/UserActionResultPopup.vue";


@Component({
  components: {
    ApplicationToolbar,
    ApplicationOverlay,
    NotificationPopup,
    UserActionResultPopup,
    StartupModal,
  }
})
export default class App extends Vue {

  public socket!: WebSocket;
  public activeErrorSub!: Subscription;
  public stateSub!: Subscription;
  public notificationSub!: Subscription;
  public notificationCounter: number = 0;
  public userActivityTracker!: any;
  // Timeout for tracking user activity
  private userActivityTimeout: number = 3600000;
  // Debounce timeout that is applied to the user activity tracker
  private USER_ACTIVITY_DEBOUNCE_TIMEOUT: number = 1000;
  private ACTIVE_ERROR_DEBOUNCE_MS: number = 500;
  private activeErrors: any[] = [];

  public created() {
    EventBus.$on(EVENTBUS_TOPICS.AUTHENTICATED, () => {
      // Clear and reset the activity tracker so it doesn't execute the debounced reset before login/logout
      clearTimeout(this.userActivityTracker);
      this.resetUserActivityTracker();
    });

    this.setActiveErrorsDebounced = debounce(this.setActiveErrorsDebounced, this.ACTIVE_ERROR_DEBOUNCE_MS);

    // Debounced activity tracker so it will only execute every x milliseconds
    this.resetUserActivityTracker = debounce(
        this.resetUserActivityTracker,
        this.USER_ACTIVITY_DEBOUNCE_TIMEOUT
    );
  }

  public mounted() {
    // App is loaded, connect to backend websocket
    this.connectToBackend();

    // Start user activity timeout tracker
    this.startUserActivityTracker();

    EventBus.$on(EVENTBUS_TOPICS.NOTIFICATION, (notification: any) => {
      this.addNotification(notification);
    });
  }

  public beforeDestroy() {
    if (this.activeErrorSub !== undefined) {
      this.activeErrorSub.unsubscribe();
    }

    if (this.stateSub !== undefined) {
      this.stateSub.unsubscribe();
    }

    if (this.notificationSub !== undefined) {
      this.notificationSub.unsubscribe();
    }

    EventBus.$off(EVENTBUS_TOPICS.NOTIFICATION);
    EventBus.$off(EVENTBUS_TOPICS.AUTHENTICATED);

    // Stop user activity timeout tracker
    clearTimeout(this.userActivityTracker);
  }

  public connectToBackend() {
    this.socket = new SockJS("/ws");
    this.$store.state.stompClient = Stomp.over(this.socket);

    // Disable Stomp debug output to console
    this.$store.state.stompClient.debug = () => {
      // Just a comment to make TSLint happy about this empty method body...
    };

    this.$store.state.stompClient.connect(
        {},
        (frame: any) => {
          // Successfully connected, notify our components they can (re-)subscribe
          EventBus.$emit(EVENTBUS_TOPICS.WEBSOCKET_CONNECTED, true);
          this.websocketConnected();
        },
        (error: any) => {
          // We cannot connect or lost our active connection to the backend, queue a retry
          // Also add some modal to the template to indicate the connection problem.
          EventBus.$emit(EVENTBUS_TOPICS.WEBSOCKET_CONNECTED, false);
          setTimeout(this.connectToBackend, 5000);
        }
    );
  }

  /**
   * Prevent flickering because of warnings which are active extremely shortly (PLC does this)
   */
  public setActiveErrorsDebounced() {
    this.$store.state.activeErrors = this.activeErrors;
  }

  public websocketConnected() {
    this.getIsProductionProfileActive();

    this.activeErrorSub = this.$store.state.stompClient.subscribe(
        STOMP_TOPICS.ACTIVE_ERRORS,
        (message: Message) => {
          this.activeErrors = [];
          for (const err of JSON.parse(message.body)) {
            this.activeErrors.push(new ErrorLog(err));
          }

          this.setActiveErrorsDebounced();
        }
    );

    this.stateSub = this.$store.state.stompClient.subscribe(
        STOMP_TOPICS.IV_STATE,
        (message: Message) => {
          this.$store.state.cubiScanState = JSON.parse(message.body);
          EventBus.$emit(EVENTBUS_TOPICS.IV_STATE_CHANGED, this.$store.state.cubiScanState);
        }
    );

    this.notificationSub = this.$store.state.stompClient.subscribe(
        STOMP_TOPICS.NOTIFICATION,
        (message: Message) => {
          this.notificationCounter++;
          const notification = JSON.parse(message.body);
          this.addNotification(notification);
        }
    );
  }

  public startUserActivityTracker() {
    AXIOS.get("/properties/auto-log-off-timeout").then((message) => {
      this.userActivityTimeout = message.data * 1000;
    });
  }

  public getIsProductionProfileActive() {
    AXIOS.get("/is-production-profile-active").then((message) => {
      this.$store.state.productionProfileActive = message.data;

      // If the current page is a development feature, but production profile is active, route to home
      if (this.$store.state.productionProfileActive && this.$router.currentRoute.matched.find((r) => r.meta && r.meta.developmentFeature)) {
        // Route to home
        this.$router.push("/");
      }
    });
  }

  public resetUserActivityTracker() {
    // Reset the tracker after user activity is detected
    if (this.$store.getters.getUser) {
      clearTimeout(this.userActivityTracker);
      this.userActivityTracker = setTimeout(() => {
        this.idleUserAction();
      }, this.userActivityTimeout);
    }
  }

  public idleUserAction() {
    // Action when a user is idle for too long
    // Sign out user
    this.$store.dispatch("userSignOut");
    // Route to home page
    this.$router.push("/").catch((err) => {
      // Just a comment to make TSLint happy about this empty method body...
    });
    // Show notificition that user is signed out due to inactivity
    EventBus.$emit(EVENTBUS_TOPICS.NOTIFICATION, this.$t("auth.autoLogOffNotification"));
  }

  public onDocumentClick(event: any) {
    this.resetUserActivityTracker();
  }

  public addNotification(notification: any) {
    notification.id = this.notificationCounter++;
    notification.timeOccured = new Date();
    this.$store.state.notificationQueue.unshift(notification);
    EventBus.$emit(EVENTBUS_TOPICS.NEW_NOTIFICATION, notification);
  }
}
